import { getDomain } from 'tldts';

/**
 * Why a submitted domain was refused.
 *
 * The values the server uses, because the reason is a telemetry dimension: a
 * client that invented its own vocabulary would split the `Domain Rejected`
 * funnel in two and neither half would be readable.
 */
export const DOMAIN_REJECTION_REASONS = [
  'empty',
  'localhost',
  'ip_address',
  'no_tld',
  'malformed',
  'subdomain',
] as const;

export type DomainRejectionReason = typeof DOMAIN_REJECTION_REASONS[number];

export type NormalizedDomainInput =
  | { ok: true; domain: string; host: string }
  | { ok: false; reason: Exclude<DomainRejectionReason, 'subdomain'> };

/** What the customer reads for each refusal. */
export const DOMAIN_REJECTION_MESSAGES: Record<DomainRejectionReason, string> = {
  empty: 'Enter the domain you want to set up.',
  localhost: 'Enter a public domain. A local address cannot be set up.',
  ip_address: 'Enter a domain name rather than an IP address.',
  no_tld: 'Enter a full domain, for example yoursite.com.',
  malformed: 'That does not look like a domain. Check it and try again.',
  subdomain: "Subdomains aren't supported in the beta yet.",
};

const MAX_DOMAIN_LENGTH = 253;
const MAX_LABEL_LENGTH = 63;
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const IPV4_LIKE = /^\d{1,3}(?:\.\d{1,3}){3}$/;

/**
 * Hosting platforms the Public Suffix List does not list, so a site on one
 * stays whole: `mystore.wordpress.com` must not reduce to `wordpress.com`,
 * which is somebody else's zone. Mirrored in rest-api's `normalizeDomain.ts`.
 */
const UNLISTED_PLATFORM_SUFFIXES = new Set([
  'amazonaws.com', 'bigcartel.com', 'ghost.io', 'glitch.me', 'hs-sites.com',
  'railway.app', 'squarespace.com', 'strikingly.com', 'substack.com', 'surge.sh',
  'wordpress.com',
]);

function rootDomainOf(validatedHost: string): string {
  const labels = validatedHost.split('.');
  const lastTwo = labels.slice(-2).join('.');
  if (labels.length > 2 && UNLISTED_PLATFORM_SUFFIXES.has(lastTwo)) {
    return labels.slice(-3).join('.');
  }

  return (
    getDomain(validatedHost, {
      allowPrivateDomains: true,
      extractHostname: false,
      validateHostname: false,
    }) ?? validatedHost
  );
}

/**
 * Punycode, without a dependency.
 *
 * `URL` already implements IDNA, so a hostname round-tripped through it comes
 * back as ASCII. It is also exactly what the browser will do with the value, so
 * the client and the server agree on the same encoder.
 */
function toAscii(host: string): string | null {
  try {
    const { hostname } = new URL(`https://${host}`);

    return hostname === '' ? null : hostname;
  } catch {
    return null;
  }
}

/**
 * Turns whatever the customer typed into the root domain the flow will act on.
 *
 * One validator, because the dashboard had two that disagreed:
 * `EnterDomainForm` accepted `https://localhost` (its blacklist compared the
 * raw input with `includes`), and `AddDomainModal`'s antd `type: 'url'` rule
 * rejected a bare `example.com`. A customer could be refused on one screen and
 * accepted on the other, for the same site.
 *
 * `EnterDomainForm` now validates through this helper. It keeps sending the
 * host the customer typed to the verification call, because reducing to the
 * root would move which host is verified for every customer in the wizard —
 * see the note on that form. `AddDomainModal` is out of scope (PRE-3289).
 *
 * The rejection reasons and the messages mirror the server's (PRE-3283),
 * because the reason is a telemetry dimension.
 */
export function normalizeDomainInput(input: string): NormalizedDomainInput {
  const trimmed = (input ?? '').trim();
  if (trimmed === '') return { ok: false, reason: 'empty' };

  const withoutScheme = trimmed.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');
  // A pasted URL is the common input here, not a mistake: everything from the
  // first `/`, `?` or `#` is a path.
  const hostAndPort = withoutScheme.split(/[/?#]/)[0];
  const withoutCredentials = hostAndPort.slice(hostAndPort.lastIndexOf('@') + 1);
  const withoutPort = withoutCredentials.replace(/:\d+$/, '');
  const lowercased = withoutPort.toLowerCase();
  const withoutTrailingDot = lowercased.replace(/\.$/, '');
  const withoutWww = withoutTrailingDot.replace(/^(?:www\.)+/, '');

  if (withoutWww === '') return { ok: false, reason: 'empty' };

  // Checked on the stripped value, so `https://localhost/` is refused. The old
  // blacklist compared the raw input, which let exactly that through.
  if (withoutWww === 'localhost' || withoutWww.endsWith('.localhost')) {
    return { ok: false, reason: 'localhost' };
  }

  if (withoutWww.startsWith('[') || IPV4_LIKE.test(withoutWww)) {
    return { ok: false, reason: 'ip_address' };
  }

  // Checked before encoding, because `URL` treats a trailing all-numeric label
  // as an attempted IPv4 address and throws — which would report `example.123`
  // as `malformed` here and `no_tld` on the server. The reason is a telemetry
  // dimension, so the two must agree.
  const rawLabels = withoutWww.split('.');
  if (/^\d+$/.test(rawLabels[rawLabels.length - 1])) {
    return { ok: false, reason: 'no_tld' };
  }

  const ascii = toAscii(withoutWww);
  if (ascii === null || ascii.length > MAX_DOMAIN_LENGTH) {
    return { ok: false, reason: 'malformed' };
  }

  // `URL` accepts an IP literal as a hostname, so the check runs again on the
  // encoded form — `0x7f.0.0.1` arrives here as `127.0.0.1`.
  if (IPV4_LIKE.test(ascii) || ascii.startsWith('[')) {
    return { ok: false, reason: 'ip_address' };
  }

  const labels = ascii.split('.');
  if (labels.length < 2) return { ok: false, reason: 'no_tld' };

  if (
    labels.some((label) => label.length > MAX_LABEL_LENGTH || !LABEL.test(label))
  ) {
    return { ok: false, reason: 'malformed' };
  }

  const tld = labels[labels.length - 1];
  if (!/^[a-z]{2,}$/.test(tld)) return { ok: false, reason: 'no_tld' };

  return { ok: true, domain: rootDomainOf(ascii), host: ascii };
}
